import { Text } from '@amzn/stencil-react-components/text';

import {
  AuditCriteriaStep,
  type CriteriaField,
  type CriteriaAttachments,
} from './AuditCriteriaStep';
import { SubQuestionsStep } from './SubQuestionsStep';
import { QuestionConfigurationStep } from './QuestionConfigurationStep';
import { ReviewWizardStep, type ReviewQuestion } from '../logic/reviewTypes';
import type {
  CriteriaFieldErrors,
  QuestionConfigFieldErrors,
} from '../../../shared/domain/questionConfigValidation';

export interface ActiveStepEditorProps {
  /** The wizard step currently in view — selects which editor renders. */
  readonly step: ReviewWizardStep;
  /** The draft being edited, or undefined when the list is empty. */
  readonly question: ReviewQuestion | undefined;
  /** Commits an edited draft back to the wizard. */
  readonly onQuestionChange: (next: ReviewQuestion) => void;
  /** Published drafts are read-only on the review screen. */
  readonly disabled: boolean;
  /** The active draft's criteria attachments (reference-stable empty fallback
   *  resolved by the caller). */
  readonly attachments: CriteriaAttachments;
  /** Criteria attachment handler (UI-only in Phase 1). */
  readonly onAttachmentsChange: (field: CriteriaField, files: File[]) => void;
  /** Per-field criteria validation errors for the criteria step. */
  readonly criteriaFieldErrors: CriteriaFieldErrors;
  /** Per-field config validation errors for the configuration step. */
  readonly configFieldErrors: QuestionConfigFieldErrors;
  /** Copy shown when there is no draft to edit (empty list). */
  readonly emptyLabel: string;
  /** Test id prefix threaded to the active step editor. */
  readonly dataTestId: string;
}

/**
 * Renders the editor for the active review-wizard step (AuditWhiz WHIZ-002).
 *
 * A thin step dispatcher extracted from `ReviewQuestionsPage` so the page stays
 * under the file-size limit and the step selection is unit-testable on its own.
 * Early-returns per step (no nested ternary in JSX, per the tsx-readability
 * rule) and guards the empty-list case where no draft is selected. All data
 * (attachments, field errors) is resolved by the caller and passed in, so this
 * component owns no wizard state.
 */
export function ActiveStepEditor({
  step,
  question,
  onQuestionChange,
  disabled,
  attachments,
  onAttachmentsChange,
  criteriaFieldErrors,
  configFieldErrors,
  emptyLabel,
  dataTestId,
}: ActiveStepEditorProps): JSX.Element {
  if (!question) {
    return <Text color="color.text.secondary">{emptyLabel}</Text>;
  }
  const stepProps = { question, onQuestionChange, disabled, dataTestId };
  if (step === ReviewWizardStep.AUDIT_CRITERIA) {
    return (
      <AuditCriteriaStep
        {...stepProps}
        attachments={attachments}
        onAttachmentsChange={onAttachmentsChange}
        fieldErrors={criteriaFieldErrors}
      />
    );
  }
  if (step === ReviewWizardStep.SUB_QUESTIONS) {
    return <SubQuestionsStep {...stepProps} />;
  }
  return <QuestionConfigurationStep {...stepProps} fieldErrors={configFieldErrors} />;
}
